/**
 * SharedPatientsWidget — iter570 (Phase 7c)
 *
 * Показва списък с пациентите, които са одобрили read-only invite към
 * текущия лекар (via magic-link flow в `doctor_sharing.py`).
 *
 * GET /api/doctor-share/my-patients  →  { items: [...] }
 * Click → /doctor/shared/:patient_id (нова read-only страница)
 *
 * UX:
 *   • Малък card-style widget — синхронен с тона на doctor dashboard
 *   • Auto-hide ако няма shared patients (не разваля empty state)
 *   • BG-first, без чуждици: "Споделени с мен пациенти"
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Loader2, Link2, ChevronRight, ShieldCheck, Calendar, LogIn } from "lucide-react";

export default function SharedPatientsWidget() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";

  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const token = localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
      const { data } = await axios.get(`${API}/doctor-share/my-patients`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setItems(data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || (bg ? "Грешка при зареждане" : "Failed to load"));
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, [API, bg]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Loading state
  if (!loaded) {
    return (
      <div className={`rounded-2xl border p-4 ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"}`}
           data-testid="shared-patients-widget-loading">
        <div className="flex items-center gap-2 text-xs">
          <Loader2 className={`w-4 h-4 animate-spin ${isDark ? "text-violet-300" : "text-violet-600"}`} />
          <span className={isDark ? "text-gray-400" : "text-gray-600"}>
            {bg ? "Зареждам споделени пациенти…" : "Loading shared patients…"}
          </span>
        </div>
      </div>
    );
  }

  // Empty — render компактен informational card (помага на лекари да видят че feature-ът съществува)
  if (items.length === 0) {
    return (
      <div
        className={`rounded-2xl border p-4 ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"}`}
        data-testid="shared-patients-widget-empty"
      >
        <div className="flex items-center gap-2 mb-1.5">
          <Link2 className={`w-4 h-4 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Споделени с мен пациенти" : "Patients shared with me"}
          </h3>
        </div>
        <p className={`text-[11px] leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg
            ? "Пациент може да Ви изпрати покана от своя профил → ще се появи тук след одобрение."
            : "Patients can send you an invite from their profile → it will appear here once approved."}
        </p>
        {error && (
          <p className={`mt-2 text-[10px] ${isDark ? "text-rose-300" : "text-rose-600"}`}>{error}</p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"}`}
      data-testid="shared-patients-widget"
    >
      <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? "border-white/5" : "border-gray-100"}`}>
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className={`w-4 h-4 shrink-0 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Споделени с мен пациенти" : "Patients shared with me"}
          </h3>
        </div>
        <span
          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
            isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"
          }`}
          data-testid="shared-patients-count"
        >
          {items.length}
        </span>
      </div>

      <ul className="divide-y divide-gray-100 dark:divide-white/5">
        {items.map((it) => {
          const approved = it.approved_at ? new Date(it.approved_at) : null;
          const approvedLabel = approved
            ? approved.toLocaleDateString(bg ? "bg-BG" : "en-US", { day: "numeric", month: "short", year: "numeric" })
            : "—";

          const impersonateReadonly = async (e) => {
            e.stopPropagation();
            const ok = window.confirm(
              bg
                ? `Влизане в акаунта на ${it.patient_name || "пациента"} в режим само за четене?\n\nЩе виждате точно това, което вижда пациентът. Не може да променяте нищо. Действието се записва в audit лога.`
                : `Enter ${it.patient_name || "patient"} account in read-only mode?\n\nYou will see exactly what the patient sees. No writes allowed. This action is audit-logged.`
            );
            if (!ok) return;
            try {
              const doctorToken = localStorage.getItem("access_token")
                || localStorage.getItem("nutramilo_access_token")
                || sessionStorage.getItem("nutramilo_access_token");
              const doctorRefresh = sessionStorage.getItem("nutramilo_refresh_token") || "";
              const { data } = await axios.post(
                `${API}/doctor-share/patient/${it.patient_id}/impersonate`,
                {},
                { headers: doctorToken ? { Authorization: `Bearer ${doctorToken}` } : {} },
              );
              const { access_token, target_user } = data || {};
              if (!access_token) throw new Error("Няма получен токен");
              localStorage.setItem("impersonation_admin_token", doctorToken || "");
              localStorage.setItem("impersonation_admin_refresh", doctorRefresh);
              localStorage.setItem("impersonation_target_email", target_user?.email || "");
              localStorage.setItem("impersonation_active", "true");
              localStorage.setItem("impersonation_readonly", "true");
              localStorage.setItem("impersonation_return_path", "/doctor");
              localStorage.setItem("access_token", access_token);
              sessionStorage.setItem("nutramilo_access_token", access_token);
              sessionStorage.removeItem("nutramilo_refresh_token");
              window.location.href = "/dashboard";
            } catch (err) {
              const msg = err?.response?.data?.detail || err.message || "Грешка";
              window.alert((bg ? "Грешка при преглед: " : "Error viewing: ") + msg);
            }
          };

          return (
            <li key={it.id}>
              <div className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${
                isDark ? "hover:bg-white/[0.03]" : "hover:bg-gray-50"
              }`}>
                <button
                  type="button"
                  onClick={() => navigate(`/doctor/shared/${it.patient_id}`)}
                  data-testid={`shared-patient-row-${it.patient_id}`}
                  className="flex items-center gap-3 text-left flex-1 min-w-0"
                >
                  <div
                    className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center font-bold text-sm ${
                      isDark
                        ? "bg-gradient-to-br from-violet-600/40 to-fuchsia-600/40 text-violet-200"
                        : "bg-gradient-to-br from-violet-100 to-fuchsia-100 text-violet-700"
                    }`}
                  >
                    {(it.patient_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-semibold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {it.patient_name || (bg ? "Пациент" : "Patient")}
                    </div>
                    <div className={`text-[11px] flex items-center gap-1 mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      <Calendar className="w-3 h-3" />
                      {bg ? "Одобрено:" : "Approved:"} {approvedLabel}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
                </button>
                <button
                  type="button"
                  onClick={impersonateReadonly}
                  data-testid={`shared-patient-impersonate-${it.patient_id}`}
                  title={bg ? "Виж точно каквото вижда пациентът (само за четене)" : "See exactly what the patient sees (read-only)"}
                  className={`shrink-0 inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-bold ${
                    isDark
                      ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/30"
                      : "bg-sky-100 text-sky-700 hover:bg-sky-200"
                  }`}
                >
                  <LogIn className="w-3 h-3" />
                  {bg ? "Влез (RO)" : "View (RO)"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
